import { DEFAULT_FORMAT_DATE } from "constants/datetime";
import { ModeForm } from "constants/enum";
import { RequestStatusSelect } from "constants/enumSelect/requestSelect";

import { useRef, useState } from "react";

import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Grid } from "@mui/material";
import {
  Badge,
  Button,
  DatePickerHookForm,
  DayjsLocalizationProvider,
  FormContainerControlled,
  Save01Icon,
  SelectHookForm,
  Send01Icon,
  TextFieldHookForm,
  Typography,
} from "components";
import { Breadcrumbs } from "components/Breadcrumbs/Breadcrumbs";
import ClientSelectForm from "components/SelectApi/ClientSelectForm";
import UserSelectForm from "components/SelectApi/UserSelectForm";
import { primary } from "configs/colors";
import { confirm } from "contexts";
import {
  RequestEntity,
  RequestEntityStatus,
  RequestHistoryEntity,
  useAppControllerMe,
} from "generated";
import { dayjsTZ } from "helpers";
import { getColorRequestEntityStatus } from "helpers/string";
import { stringRequired } from "helpers/yup";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";

import { RequestCandidateList } from "./RequestCandidateList";

const FormSchema = Yup.object({
  request: Yup.object({
    name: stringRequired,
  }),
});

interface Props {
  data?: RequestEntity;
  title: string;
  handleSubmitForm: (args: any) => void;
  handleSendRequest?: () => void;
  isRefetch?: boolean;
  mode: ModeForm;
  history?: RequestHistoryEntity;
}

export const RequestForm = (props: Props) => {
  const navigate = useNavigate();
  const {
    title,
    data,
    handleSubmitForm,
    mode,
    isRefetch,
    handleSendRequest,
    history,
  } = props;

  const [disableRequestField, setDisableRequestField] =
    useState<boolean>(false);
  const refContainer = useRef(dayjsTZ(new Date()));

  const handleReason = (arr: string[]) => {
    let result = "";
    arr.forEach((e: string, index: number) => {
      result += `${index === 0 || index === arr.length ? "" : "\n"}${e}`;
    });
    return result;
  };

  const methods = useForm<RequestEntity | any>({
    values: {
      request: {
        name: data?.name ?? "",
        code: data?.code ?? "",
        requestor: data?.requestor ?? "",
        requestDate: data?.createdAt
          ? dayjsTZ(data?.createdAt)
          : refContainer.current,
        clientId: data?.client
          ? {
              value: data?.client?.id,
              label: data?.client?.name,
            }
          : null,
        legalName: data?.client ? data?.client?.legalName : "",
        dueDate: data?.dueDate ? dayjsTZ(data?.dueDate) : undefined,
        status: data?.status ?? "Draft",
        description: data?.description ?? "",
        contractor: data?.contractor ?? undefined,
      },
      reason: history?.reason
        ? handleReason(JSON.parse(history.reason) as string[])
        : "",
    },
    resolver: yupResolver(FormSchema),
  });

  const onSubmit = (dataSubmit: any) => {
    handleSubmitForm(dataSubmit);
  };

  const { setValue } = methods;
  const disable =
    mode === ModeForm.View ||
    data?.status === RequestEntityStatus.WaitingForApproval;
  // eslint-disable-next-line
  const { data: me } = useAppControllerMe({
    query: {
      onSuccess(dataUser) {
        if (mode === ModeForm.Create) {
          setValue("request.requestor", dataUser?.data?.email);
          if (dataUser?.data?.client) {
            setValue("request.clientId", {
              value: dataUser?.data?.client.id,
              label: dataUser?.data?.client?.name,
            });
            setValue("request.legalName", dataUser?.data?.client?.legalName);
          }
        }
      },
    },
  });

  const handleButtonSubmit = (modeForm: ModeForm) => {
    if (modeForm === ModeForm.Create) {
      return "Save Draft";
    }
    if (modeForm === ModeForm.Edit) {
      return "Save";
    }
    return "";
  };
  return (
    <>
      <FormContainerControlled methods={methods} onSubmit={onSubmit}>
        <Breadcrumbs
          rightComponent={
            <>
              <Button
                variant="outlined"
                sx={{
                  marginRight: "8px",
                }}
                onClick={() => {
                  navigate("/request");
                }}
              >
                Back
              </Button>

              {mode === ModeForm.View ? null : (
                <>
                  <Button
                    startIcon={<Send01Icon />}
                    variant="outlined"
                    sx={{
                      marginRight: "8px",
                      backgroundColor: primary[50],
                      "&:hover": {
                        backgroundColor: primary[200],
                        boxShadow: "none",
                      },
                      display:
                        data?.status === RequestEntityStatus.Draft ||
                        data?.status === RequestEntityStatus.Rejected
                          ? undefined
                          : "none",
                    }}
                    disabled={mode === ModeForm.Create}
                    onClick={() => {
                      if (handleSendRequest) {
                        confirm({
                          title: "Request",
                          content: "Do you want to request?",
                        })
                          .then(() => {
                            handleSendRequest();
                          })
                          .catch(() => {});
                      }
                    }}
                  >
                    Request
                  </Button>
                  <Button
                    type="submit"
                    startIcon={<Save01Icon />}
                    sx={{
                      display:
                        data?.status === RequestEntityStatus.Draft ||
                        data?.status === RequestEntityStatus.New ||
                        data?.status === RequestEntityStatus.Rejected ||
                        mode === ModeForm.Create
                          ? undefined
                          : "none",
                    }}
                  >
                    {handleButtonSubmit(mode)}
                  </Button>
                </>
              )}
            </>
          }
        />
        <Box
          sx={{
            backgroundColor: "#F9FAFB",
            padding: "14px",
            borderRadius: "12px",
          }}
        >
          <Typography type="TextLG" fontWeight={600}>
            {title}
          </Typography>
          <Grid container spacing="14px" marginTop="0">
            <Grid item xs={12} lg={4}>
              <ClientSelectForm
                name="request.clientId"
                label="Client Name"
                disabled
                onChange={(e) => {
                  if (e) {
                    if (e?.info?.legalName) {
                      setValue("request.legalName", e?.info?.legalName);
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} lg={4}>
              <TextFieldHookForm
                name="request.legalName"
                label="Legal Name"
                disabled
              />
            </Grid>
            <Grid item xs={12} lg={4}>
              <UserSelectForm
                name="request.requestor"
                label="Requestor"
                placeholder="Client's Contact"
                disabled
              />
            </Grid>
            <Grid item xs={12} lg={4}>
              <TextFieldHookForm
                required
                name="request.name"
                label="Request Name"
                placeholder="Enter a Name"
                disabled={disable || disableRequestField}
              />
            </Grid>
            <Grid item xs={12} lg={4}>
              <TextFieldHookForm
                name="request.code"
                label="Request Code"
                placeholder="Auto generate"
                disabled
              />
            </Grid>
            <Grid item xs={12} lg={4}>
              <TextFieldHookForm
                name="request.contractor"
                label="Contractor"
                placeholder="Enter a Contractor"
                disabled={disable || disableRequestField}
              />
            </Grid>
            <Grid item xs={12} lg={4}>
              {/* {mode === ModeForm.Create ? null : ( */}
              <DayjsLocalizationProvider>
                <DatePickerHookForm
                  format={DEFAULT_FORMAT_DATE}
                  name="request.requestDate"
                  label="Request date"
                  placeholder="Select date"
                  disabled
                />
              </DayjsLocalizationProvider>
              {/* )} */}
            </Grid>
            <Grid item xs={12} lg={4}>
              <DayjsLocalizationProvider>
                <DatePickerHookForm
                  format={DEFAULT_FORMAT_DATE}
                  name="request.dueDate"
                  label="Due Date"
                  placeholder="Select date"
                  disabled={disable || disableRequestField}
                />
              </DayjsLocalizationProvider>
            </Grid>
            <Grid item xs={12} lg={4}>
              <SelectHookForm
                name="request.status"
                label="Status"
                options={RequestStatusSelect}
                disabled
              />
            </Grid>
            <Grid item xs={12} lg={12}>
              <TextFieldHookForm
                name="request.description"
                label="Remark"
                placeholder="e.g. Explaination for this field"
                multiline
                rows={3}
                disabled={disable || disableRequestField}
              />
            </Grid>
          </Grid>
        </Box>
        {mode !== ModeForm.Create && (
          <Box
            sx={{
              marginTop: "14px",
              backgroundColor: "#F9FAFB",
              padding: "14px",
              borderRadius: "12px",
            }}
          >
            <Typography type="TextLG" fontWeight={600} marginBottom="14px">
              Latest action
            </Typography>
            <Grid display="flex" alignItems="center">
              <Grid marginRight="14px">
                <Badge
                  dot
                  size="small"
                  color={getColorRequestEntityStatus(data?.status)} // eslint-disable-line
                >
                  {data?.status}
                </Badge>
              </Grid>
              <Box component="span" fontSize="14px" sx={{ display: "block" }}>
                <Box component="span" fontWeight={600}>
                  {history?.action}&nbsp;
                </Box>
                by&nbsp;
                <Box component="span" fontWeight={600}>
                  {history?.createdBy?.fullName}&nbsp;
                </Box>
                <Box component="span">({history?.createdBy?.email})</Box>
              </Box>
            </Grid>
            {history?.reason && (
              <Grid marginTop="14px">
                <TextFieldHookForm
                  name="reason"
                  placeholder="Enter information..."
                  label="Reason"
                  multiline
                  rows={3}
                  disabled
                />
              </Grid>
            )}
          </Box>
        )}
      </FormContainerControlled>
      {mode === ModeForm.Create ? null : (
        <Box
          sx={{
            marginTop: "14px",
            backgroundColor: "#F9FAFB",
            padding: "14px",
            borderRadius: "12px",
          }}
        >
          <RequestCandidateList
            isRefetch={!!isRefetch}
            setDisableRequestField={setDisableRequestField}
            dataRequest={data as RequestEntity}
          />
        </Box>
      )}
    </>
  );
};
